import { useState, useEffect, useRef } from 'react'

interface VideoProgressBarProps {
  videoRef: HTMLVideoElement | null
  isActive: boolean
}

export function VideoProgressBar({ videoRef, isActive }: VideoProgressBarProps) {
  const [progress, setProgress] = useState(0)
  const rafIdRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (!videoRef || !isActive) {
      setProgress(0)
      return
    }

    let isRunning = true

    const updateProgress = () => {
      if (!isRunning || !videoRef) return

      if (videoRef.duration && !videoRef.paused) {
        const percentage = (videoRef.currentTime / videoRef.duration) * 100
        setProgress(percentage)
      }

      // Continue updating while video is playing
      rafIdRef.current = requestAnimationFrame(updateProgress)
    }

    const handlePlay = () => {
      isRunning = true
      rafIdRef.current = requestAnimationFrame(updateProgress)
    }

    const handlePause = () => {
      isRunning = false
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current)
      }
    }

    const handleEnded = () => {
      setProgress(0)
      isRunning = false
    }

    const handleSeeked = () => {
      if (videoRef.duration) {
        const percentage = (videoRef.currentTime / videoRef.duration) * 100
        setProgress(percentage)
      }
    }

    videoRef.addEventListener('play', handlePlay)
    videoRef.addEventListener('pause', handlePause)
    videoRef.addEventListener('ended', handleEnded)
    videoRef.addEventListener('seeked', handleSeeked)

    // Start animation loop if video is already playing
    if (!videoRef.paused) {
      rafIdRef.current = requestAnimationFrame(updateProgress)
    }

    return () => {
      isRunning = false
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current)
      }
      videoRef.removeEventListener('play', handlePlay)
      videoRef.removeEventListener('pause', handlePause)
      videoRef.removeEventListener('ended', handleEnded)
      videoRef.removeEventListener('seeked', handleSeeked)
    }
  }, [videoRef, isActive])

  return (
    <div className="absolute top-0 left-0 right-0 h-1 bg-white/10 z-50 md:max-w-[440px] md:mx-auto">
      <div
        className="h-full bg-white transition-none"
        style={{ width: `${progress}%` }}
      />
    </div>
  )
}
